import React, { useMemo, useState } from 'react';
import { Clock, ArrowRight, Search, X, LayoutGrid, List } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import { BLOG_CATEGORIES_I18N, BLOG_UI, formatBlogDate } from '../data/blogArticles';
import { useLanguage } from '../i18n/LanguageContext';
import { useBlog } from '../contexts/BlogContext';
import { AdSlot } from '../components/ads/AdSlot';
interface BlogListPageProps {
  onNavigate: (page: string) => void;
}
export function BlogListPage({
  onNavigate
}: BlogListPageProps) {
  const {
    language
  } = useLanguage();
  const {
    allArticles
  } = useBlog();
  const ui = BLOG_UI[language];
  const categories = BLOG_CATEGORIES_I18N[language];
  const ruCategories = BLOG_CATEGORIES_I18N.ru;
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0); // 0 is 'All'
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const articlesPerPage = viewMode === 'cards' ? 6 : 10;
  // Filter articles
  const filteredArticles = useMemo(() => {
    return allArticles.filter(article => {
      // Category filter (using Russian category names from data)
      if (activeCategoryIndex !== 0 && article.category !== ruCategories[activeCategoryIndex]) {
        return false;
      }
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return article.title.toLowerCase().includes(query) || article.excerpt.toLowerCase().includes(query) || article.tags.some(tag => tag.toLowerCase().includes(query));
      }
      return true;
    });
  }, [activeCategoryIndex, searchQuery, ruCategories, allArticles]);
  // Pagination
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const paginatedArticles = filteredArticles.slice((currentPage - 1) * articlesPerPage, currentPage * articlesPerPage);
  // Featured article logic
  const isDefaultView = activeCategoryIndex === 0 && !searchQuery && currentPage === 1 && viewMode === 'cards';
  const featuredArticle = isDefaultView ? allArticles.find(a => a.featured) || allArticles[0] : null;
  // Exclude featured article from grid if in default view
  const displayArticles = isDefaultView ? paginatedArticles.filter(a => a.id !== featuredArticle?.id).slice(0, articlesPerPage) : paginatedArticles;
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  const handleCategoryClick = (index: number) => {
    setActiveCategoryIndex(index);
    setCurrentPage(1);
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-19409">
      <div className="bg-white border-b border-slate-200 pt-12 pb-12" data-id="element-19410">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-19411">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8" data-id="element-19412">
            <div data-id="element-19413">
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-19414">
                {ui.title}
              </h1>
              <p className="text-lg text-slate-600" data-id="element-19415">{ui.subtitle}</p>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-19416">
              <div className="relative w-full md:w-72" data-id="element-19417">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-19418" />
                <input type="text" value={searchQuery} onChange={handleSearch} placeholder={ui.search} className="w-full pl-10 pr-10 py-2.5 rounded-xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all" data-id="element-19419" />
                {searchQuery && <button onClick={() => handleSearch({
                target: {
                  value: ''
                }
              } as any)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-19420">
                    <X className="w-4 h-4" data-id="element-19421" />
                  </button>}
              </div>

              <div className="hidden sm:flex items-center bg-slate-100 p-1 rounded-xl" data-id="element-19422">
                <button onClick={() => {
                setViewMode('cards');
                setCurrentPage(1);
              }} className={`p-2 rounded-lg transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-primary' : 'text-slate-500 hover:text-slate-700'}`} title={ui.viewCards} data-id="element-19423">
                  <LayoutGrid className="w-5 h-5" data-id="element-19424" />
                </button>
                <button onClick={() => {
                setViewMode('table');
                setCurrentPage(1);
              }} className={`p-2 rounded-lg transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-primary' : 'text-slate-500 hover:text-slate-700'}`} title={ui.viewTable} data-id="element-19425">
                  <List className="w-5 h-5" data-id="element-19426" />
                </button>
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="flex overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 gap-2 hide-scrollbar" data-id="element-19427">
            {categories.map((cat, index) => <button key={cat} onClick={() => handleCategoryClick(index)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategoryIndex === index ? 'bg-slate-900 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-19428">
                {cat}
              </button>)}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-19429">
        {/* Featured Article (Cards view only) */}
        {featuredArticle && viewMode === 'cards' && <Card hoverable padding="none" className="mb-12 cursor-pointer group overflow-hidden" onClick={() => onNavigate(`blog-post/${featuredArticle.slug}`)} data-id="element-19430">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-19431">
              <div className={`h-64 md:h-auto ${featuredArticle.coverColor} relative overflow-hidden flex items-center justify-center`} data-id="element-19432">
                {featuredArticle.coverImage ? <img src={featuredArticle.coverImage} alt={featuredArticle.title} className="w-full h-full object-cover" data-id="element-19433" /> : <>
                    <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/10 to-transparent" data-id="element-19434"></div>
                    <span className="text-4xl md:text-6xl font-black text-slate-900/10 uppercase tracking-widest rotate-[-10deg]" data-id="element-19435">
                      {featuredArticle.category}
                    </span>
                  </>}
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-19436">
                <Badge variant="pro" className="w-fit mb-4" data-id="element-19437">
                  {ui.featured}
                </Badge>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 group-hover:text-primary transition-colors" data-id="element-19438">
                  {featuredArticle.title}
                </h2>
                <p className="text-slate-600 mb-6 line-clamp-3 leading-relaxed" data-id="element-19439">
                  {featuredArticle.excerpt}
                </p>
                <div className="flex items-center text-sm text-slate-500 mt-auto" data-id="element-19440">
                  <span data-id="element-19441">{formatBlogDate(featuredArticle.date, language)}</span>
                  <span className="mx-2" data-id="element-19442">•</span>
                  <span className="flex items-center" data-id="element-19443">
                    <Clock className="w-4 h-4 mr-1" data-id="element-19444" />{' '}
                    {featuredArticle.readTime} {ui.readTime}
                  </span>
                </div>
              </div>
            </div>
          </Card>}

        {/* Results Info */}
        {(searchQuery || activeCategoryIndex !== 0) && <div className="mb-8 text-slate-600" data-id="element-19445">
            {ui.found}:{' '}
            <span className="font-bold text-slate-900" data-id="element-19446">
              {filteredArticles.length}
            </span>
          </div>}

        {/* Content */}
        {displayArticles.length > 0 ? viewMode === 'cards' /* Grid View */ ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12" data-id="element-19447">
              {displayArticles.map(article => <Card key={article.id} hoverable padding="none" className="flex flex-col cursor-pointer group overflow-hidden" onClick={() => onNavigate(`blog-post/${article.slug}`)} data-id="element-19448">
                  <div className={`h-48 ${article.coverColor} relative overflow-hidden flex items-center justify-center`} data-id="element-19449">
                    {article.coverImage ? <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover" data-id="element-19450" /> : <>
                        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/5 to-transparent" data-id="element-19451"></div>
                        <span className="text-3xl font-black text-slate-900/10 uppercase tracking-widest rotate-[-10deg]" data-id="element-19452">
                          {article.category}
                        </span>
                      </>}
                  </div>
                  <div className="p-6 flex flex-col flex-grow" data-id="element-19453">
                    <Badge variant="neutral" className="w-fit mb-3" data-id="element-19454">
                      {article.category}
                    </Badge>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 group-hover:text-primary transition-colors line-clamp-2" data-id="element-19455">
                      {article.title}
                    </h3>
                    <p className="text-slate-600 mb-6 line-clamp-3 text-sm flex-grow leading-relaxed" data-id="element-19456">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-slate-500 mt-auto pt-4 border-t border-slate-100" data-id="element-19457">
                      <span data-id="element-19458">{formatBlogDate(article.date, language)}</span>
                      <span className="flex items-center" data-id="element-19459">
                        <Clock className="w-4 h-4 mr-1" data-id="element-19460" /> {article.readTime}
                      </span>
                    </div>
                  </div>
                </Card>)}
            </div> /* Table View */ : <div className="mb-12" data-id="element-19461">
              {/* Desktop Table */}
              <div className="hidden md:block bg-white rounded-2xl border border-slate-200 overflow-hidden" data-id="element-19462">
                <table className="w-full text-left border-collapse" data-id="element-19463">
                  <thead data-id="element-19464">
                    <tr className="bg-slate-50 border-b border-slate-200 text-sm font-semibold text-slate-600" data-id="element-19465">
                      <th className="py-4 px-6" data-id="element-19466">{ui.tableTitle}</th>
                      <th className="py-4 px-6 w-48" data-id="element-19467">{ui.tableCategory}</th>
                      <th className="py-4 px-6 w-40" data-id="element-19468">{ui.tableDate}</th>
                      <th className="py-4 px-6 w-32" data-id="element-19469">{ui.tableReadTime}</th>
                      <th className="py-4 px-6 w-16" data-id="element-19470"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-19471">
                    {displayArticles.map(article => <tr key={article.id} onClick={() => onNavigate(`blog-post/${article.slug}`)} className="hover:bg-slate-50 transition-colors cursor-pointer group" data-id="element-19472">
                        <td className="py-4 px-6" data-id="element-19473">
                          <div className="font-bold text-slate-900 mb-1 group-hover:text-primary transition-colors" data-id="element-19474">
                            {article.title}
                          </div>
                          <div className="text-sm text-slate-500 line-clamp-1" data-id="element-19475">
                            {article.excerpt}
                          </div>
                        </td>
                        <td className="py-4 px-6" data-id="element-19476">
                          <Badge variant="neutral" data-id="element-19477">{article.category}</Badge>
                        </td>
                        <td className="py-4 px-6 text-sm text-slate-600" data-id="element-19478">
                          {formatBlogDate(article.date, language)}
                        </td>
                        <td className="py-4 px-6 text-sm text-slate-600" data-id="element-19479">
                          {article.readTime}
                        </td>
                        <td className="py-4 px-6 text-right" data-id="element-19480">
                          <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-primary transition-colors" data-id="element-19481" />
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              {/* Mobile List View (for table mode) */}
              <div className="md:hidden space-y-4" data-id="element-19482">
                {displayArticles.map(article => <Card key={article.id} hoverable padding="md" onClick={() => onNavigate(`blog-post/${article.slug}`)} className="cursor-pointer group" data-id="element-19483">
                    <div className="flex justify-between items-start mb-2" data-id="element-19484">
                      <Badge variant="neutral" className="text-xs" data-id="element-19485">
                        {article.category}
                      </Badge>
                      <span className="text-xs text-slate-500" data-id="element-19486">
                        {formatBlogDate(article.date, language)}
                      </span>
                    </div>
                    <h3 className="font-bold text-slate-900 group-hover:text-primary transition-colors mb-2" data-id="element-19487">
                      {article.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-slate-500 mt-4" data-id="element-19488">
                      <span className="flex items-center" data-id="element-19489">
                        <Clock className="w-3.5 h-3.5 mr-1" data-id="element-19490" />{' '}
                        {article.readTime}
                      </span>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-primary" data-id="element-19491" />
                    </div>
                  </Card>)}
              </div>
            </div> : <div className="text-center py-20 bg-white rounded-2xl border border-slate-200 mb-12" data-id="element-19492">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" data-id="element-19493" />
            <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-19494">
              {ui.notFound}
            </h3>
            <p className="text-slate-500 mb-6" data-id="element-19495">{ui.notFoundHint}</p>
            <button onClick={() => {
          setSearchQuery('');
          setActiveCategoryIndex(0);
        }} className="text-primary font-medium hover:underline" data-id="element-19496">
              {ui.resetFilters}
            </button>
          </div>}

        {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-19497" />}

        {/* Blog Ad Banner */}
        <div className="mt-12" data-id="element-19498">
          <AdSlot slotId="SLOT-09" onNavigate={onNavigate} data-id="element-19499" />
        </div>
      </div>
    </div>;
}